import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import docTypeToModalMap from './doc-type-to-modal-map';
import { areSettingsChanged, findChangedSettings } from './helpers';
import sharedMessages from './shared-messages';
import style from './style.module.less';
import { PreviewLabelModal } from '../../../../components/modals/preview-label-modal';
import { SimpleModal } from '../../../../components/simple-modal';
import { DOCUMENT_OPTIONS_CONFIG_COMPLETE } from '../../../../constants/analytics-events';
import { DocumentType } from '../../../../enums/document-type';
import { useModal } from '../../../../hooks/use-modal';
import { Segment } from '../../../../services/analytics-services/segment';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../api/save-settings';

/**
 * @typedef {import('../../../../prop-types/document-type').DocumentTypePropType} DocumentTypePropType
 */
/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {DocumentTypePropType} props.docType
 * @returns {JSX.Element}
 */

export const DocumentOptionsModal = ({ onClose, docType }) => {
  const dispatch = useDispatch();

  const DocumentOptionsModalBody = docTypeToModalMap[docType];
  const modalTitleMessage = sharedMessages[docType];
  const isLabelFormatModal = docType === DocumentType.Label.identifier;

  const [initialSettingsState, setInitialSettingsState] = useState({});
  const [settingsState, setSettingsState] = useState(initialSettingsState);
  const [isLoading, setIsLoading] = useState(false);

  const { close: closePreviewModal, open: openPreviewModal, isOpen: isPreviewOpen } = useModal();

  const onDropdownChange = useCallback(
    (id, value) => {
      const newSettings = { ...settingsState };
      newSettings[id] = value;
      setSettingsState(newSettings);
    },
    [setSettingsState, settingsState],
  );

  const onCheckboxChange = useCallback(
    (isChecked, event) => {
      const { name } = event.target;
      const newSettings = { ...settingsState };
      newSettings[name] = isChecked;
      setSettingsState(newSettings);
    },
    [setSettingsState, settingsState],
  );

  const onInit = useCallback((initialState) => {
    setInitialSettingsState(initialState);
    setSettingsState(initialState);
  }, []);

  const onRadioCheckListChange = useCallback(
    (value, key) => {
      const newSettings = { ...settingsState };
      newSettings[key] = value;
      setSettingsState(newSettings);
    },
    [setSettingsState, settingsState],
  );

  const onInputChange = useCallback(
    (id, value) => {
      const newSettings = { ...settingsState };
      newSettings[id] = value;
      setSettingsState(newSettings);
    },
    [setSettingsState, settingsState],
  );

  const onSaveChanges = useCallback(async () => {
    if (areSettingsChanged(settingsState, initialSettingsState)) {
      setIsLoading(true);
      const changedSettings = findChangedSettings(settingsState, initialSettingsState);
      try {
        const newSettings = await updateSellerSettingsBulk(changedSettings);
        dispatch(saveNewSellerSettings(newSettings));
        Segment.trackEvent(DOCUMENT_OPTIONS_CONFIG_COMPLETE, {
          changedSettings,
          docType,
        });
      } catch {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...sharedMessages.saveSettingsErrorMessage} />,
          duration: 3000,
        });
      }
    }
    setIsLoading(false);
    onClose();
  }, [dispatch, docType, initialSettingsState, onClose, settingsState]);

  return (
    <SimpleModal
      actionMessage={sharedMessages.saveChanges}
      className={style['print-options-modal']}
      headerMessage={modalTitleMessage}
      isLoading={isLoading}
      onAction={onSaveChanges}
      onClose={onClose}
      onSave={onSaveChanges}
      onSecondAction={isLabelFormatModal ? openPreviewModal : null}
      secondActionMessage={isLabelFormatModal ? sharedMessages.preview : null}
      size='medium'
    >
      <div className={isLoading ? style.loading : style.body}>
        {isLoading ? (
          <LoadingIndicator theme='grey' />
        ) : (
          <DocumentOptionsModalBody
            onCheckboxChange={onCheckboxChange}
            onDropdownChange={onDropdownChange}
            onInit={onInit}
            onInputChange={onInputChange}
            onRadioCheckListChange={onRadioCheckListChange}
            settingsState={settingsState}
          />
        )}
        {isPreviewOpen ? <PreviewLabelModal labelFormat={settingsState.LabelOutput} onClose={closePreviewModal} /> : null}
      </div>
    </SimpleModal>
  );
};
